import { auth, db } from "../lib/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  setDoc,
  documentId,
  serverTimestamp,
  limit,
} from "@firebase/firestore";
import type {
  User,
  Group,
  Food,
  TrainingPlanTemplate,
  Activity,
  UserTrainingPlan,
  ExerciseBlock,
} from "../types";
import {
  addExerciseToFileDb,
  updateExerciseInFileDb,
  deleteExerciseFromFileDb,
} from "./exercise-db";
import { sanitizeForFirestore } from "./data";
import foodsData from "../data/foods.json";

// -----------------------------------------------------------------------------
// Helper: API call wrapper for backend-required functions
// -----------------------------------------------------------------------------
async function callApi<T = any>(
  path: string,
  data?: any,
  method = "POST"
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // The subscription endpoints only accept signed-in staff.
  const idToken = await auth.currentUser?.getIdToken();
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  const res = await fetch(path, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `API call failed: ${res.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch {
      // If not JSON, use the text as is
      if (errorText) errorMessage = errorText;
    }
    const error = new Error(errorMessage) as any;
    error.status = res.status;
    error.response = errorText;
    throw error;
  }
  return res.json() as Promise<T>;
}

// -----------------------------------------------------------------------------
// User actions
// -----------------------------------------------------------------------------

export async function findUserByIdWithAdmin(
  id: string
): Promise<User | undefined> {
  const ref = doc(db, "users", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return undefined;
  return { id: snap.id, ...snap.data() } as User;
}

export async function findUserByIdAction(id: string): Promise<User | null> {
  try {
    const user = await findUserByIdWithAdmin(id);
    return user ? JSON.parse(JSON.stringify(user)) : null;
  } catch (error) {
    console.error(`[Action] Failed to find user by ID ${id}:`, error);
    return null;
  }
}

export async function updateUserLastLoginWithAdmin(
  userId: string
): Promise<void> {
  const ref = doc(db, "users", userId);
  await updateDoc(ref, { lastLogin: new Date().toISOString() });
}



export async function updateUserProfileWithAdmin(
  userId: string,
  data: Partial<User>
): Promise<void> {
  const ref = doc(db, "users", userId);
  const safeData = sanitizeForFirestore(data);
  await updateDoc(ref, safeData);
}

export async function clearAllUserHighlightsAction(): Promise<void> {
  const { collection, getDocs, writeBatch, deleteField } = await import(
    "@firebase/firestore"
  );
  const { db } = await import("./firebase");

  const usersRef = collection(db, "users");
  const snapshot = await getDocs(usersRef);

  if (snapshot.empty) {
    return;
  }

  const batch = writeBatch(db);
  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, { highlightColor: deleteField() });
  });

  await batch.commit();
}

// -----------------------------------------------------------------------------
// Payment / subscription (require backend)
// -----------------------------------------------------------------------------

export async function createPaymentIntentAction(
  priceId: string,
  userId: string
) {
  return callApi<{ success: boolean; clientSecret?: string; error?: string }>(
    "/api/createPaymentIntent",
    { priceId, userId }
  );
}

export async function extendUserSubscriptionWithAdmin(
  userId: string,
  daysToAdd: number
): Promise<{ success: boolean }> {
  return callApi<{ success: boolean }>("/api/extendUserSubscription", {
    userId,
    daysToAdd,
  });
}

export async function reduceUserSubscriptionWithAdmin(
  userId: string,
  daysToReduce: number
): Promise<{ success: boolean }> {
  return callApi<{ success: boolean }>("/api/reduceUserSubscription", {
    userId,
    daysToReduce,
  });
}

// Ursa's automatic onboarding messages. The fixed IDs keep them from being
// sent twice (see triggerOnboardingFlowAction).
const ONBOARDING_WELCOME_ID = "onboarding-welcome";
const ONBOARDING_SETUP_ID = "onboarding-setup";
const ONBOARDING_FINAL_ID = "onboarding-final";
const ONBOARDING_WELCOME =
  "Dobrodošli u NEMA NEIDE, najjaču aplikaciju i tim na svijetu! " +
  "Ti si nam bitan i sa ovime postaješ dio ovog tima, a naša zadaća " +
  "je da te dovedemo do tvog željenog cilja. U Nema Neide rade " +
  "profesionalni treneri i natjecatelji u Bodybuildingu, naše znanje " +
  "i iskustvo je ogromno, stoga pitaj, traži, zovi, tu smo za tebe. " +
  "Zapamti, proces neće biti lagan, ali nikad nemoj odustati jer " +
  "pogaziti sebe je nešto najgore što čovjek sam sebi može napraviti. " +
  "Stoga nikad ne odustaj i neka te vodi NEMA NEIDE!!!";
const ONBOARDING_SETUP =
  "Za početak, klikni na gumb u ovoj poruci i postavi profil do " +
  "kraja — dakle postavi profilnu sliku, i onda unesi kilažu i " +
  "sliku svog izgleda! Samo prati gdje te platforma vodi. " +
  "Ako ti nešto nije jasno, slobodno ovdje pitaj!! [POSTAVI PROFIL]";
const ONBOARDING_FINAL =
  "To je to\n" +
  "Profil smo rjesili, reci mi još hoce bit trening u gymu ili kucni\n" +
  "Molim te jos napisi dali imas kakve bolesti, operacija, srčanih zastoja, kardiovaskularnih problema, dijabetesa, alergija…";

export async function triggerOnboardingFlowAction(
  newUserId: string,
  step: "initial" | "final" = "initial"
): Promise<{ success: boolean; groupId?: string }> {
  try {
    const { findUserByEmail } = await import("./data");
    const { auth } = await import("./firebase");

    // Get the current authenticated user (admin who is creating the user)
    const currentAuthUser = auth.currentUser;
    if (!currentAuthUser) {
      console.error(
        "[ACTION_LOG] Onboarding failed: No authenticated user found."
      );
      throw new Error("Onboarding failed: No authenticated user found.");
    }

    // Find trainer by email (hardcoded per Next app behavior)
    const ursaUser = await findUserByEmail("ursa2706@gmail.com");
    if (!ursaUser) {
      console.error(
        '[ACTION_LOG] Onboarding failed: Trainer account "ursa2706@gmail.com" not found.'
      );
      throw new Error("Onboarding failed: Trainer account not found.");
    }
    const ursaId = ursaUser.id;

    // Create group with admin as creator (for permission purposes)
    // but the members are still the new user and trainer
    const group = await findOrCreatePrivateGroupAction(
      newUserId,
      ursaId,
      currentAuthUser.uid // Pass admin's ID as creator
    );
    if (!group) {
      console.error(
        "[ACTION_LOG] Could not create private group for onboarding."
      );
      throw new Error("Could not create private group for onboarding.");
    }

    const groupRef = doc(db, "groups", group.id);
    const messagesRef = collection(db, "groups", group.id, "messages");

    // Onboarding gets triggered more than once for the same person (when
    // staff create the account, again on the first login, when the tour
    // ends...), which sent Ursa's messages twice. Fixed message IDs plus a
    // check for copies written by older app versions (random IDs) make each
    // step send its messages only once.
    const alreadySent = async (messageId: string, content: string) => {
      const byId = await getDoc(doc(messagesRef, messageId));
      if (byId.exists()) return true;
      const legacy = await getDocs(
        query(
          messagesRef,
          where("senderId", "==", ursaId),
          where("content", "==", content),
          limit(1)
        )
      );
      return !legacy.empty;
    };
    const preview = (content: string) =>
      content.length > 50 ? content.substring(0, 50) + "..." : content;

    if (step === "initial") {
      if (await alreadySent(ONBOARDING_WELCOME_ID, ONBOARDING_WELCOME)) {
        console.log(
          `[ACTION_LOG] Onboarding messages already sent to ${newUserId}, skipping.`
        );
      } else {
        const now = new Date().toISOString();
        // One second later so ordering by createdAt is deterministic.
        const nowSecond = new Date(Date.parse(now) + 1000).toISOString();
        const batch = writeBatch(db);
        batch.set(doc(messagesRef, ONBOARDING_WELCOME_ID), {
          type: "user",
          senderId: ursaId,
          content: ONBOARDING_WELCOME,
          groupId: group.id,
          createdAt: now,
        });
        batch.set(doc(messagesRef, ONBOARDING_SETUP_ID), {
          type: "user",
          senderId: ursaId,
          content: ONBOARDING_SETUP,
          groupId: group.id,
          createdAt: nowSecond,
        });
        batch.update(groupRef, {
          lastMessage: preview(ONBOARDING_SETUP),
          lastMessageAt: nowSecond,
        });
        await batch.commit();
        console.log(
          `[ACTION_LOG] ✅ Sent onboarding messages to ${newUserId}`
        );
      }
      return { success: true, groupId: group.id };
    }

    // Final step: Ursa's follow-up message, then mark the setup complete.
    let sentFinalMessage = false;
    try {
      if (!(await alreadySent(ONBOARDING_FINAL_ID, ONBOARDING_FINAL))) {
        const finalMsgTime = new Date().toISOString();
        const batch = writeBatch(db);
        batch.set(doc(messagesRef, ONBOARDING_FINAL_ID), {
          type: "user",
          senderId: ursaId,
          content: ONBOARDING_FINAL,
          groupId: group.id,
          createdAt: finalMsgTime,
        });
        batch.update(groupRef, {
          lastMessage: preview(ONBOARDING_FINAL),
          lastMessageAt: finalMsgTime,
        });
        await batch.commit();
        sentFinalMessage = true;
      }
    } catch (msgErr) {
      console.error(
        "[ACTION_LOG] Failed to add final onboarding message:",
        msgErr
      );
    }

    const { findUserById, updateUserProfile } = await import("./data");
    const userDoc = await findUserById(newUserId);
    if (userDoc && !userDoc.profileSetupCompleted) {
      await updateUserProfile(newUserId, { profileSetupCompleted: true });
    }
    if (userDoc && sentFinalMessage) {
      await createActivityAction(userDoc, "completed_profile_setup");
    }

    return { success: true, groupId: group.id };
  } catch (error) {
    console.error(
      `[ACTION_LOG] ❌ FAILED to trigger onboarding flow step '${step}' for user ${newUserId}:`,
      error
    );
    throw error;
  }
}

// -----------------------------------------------------------------------------
// Private chat groups
// -----------------------------------------------------------------------------

export async function findOrCreatePrivateGroupAction(
  currentUserId: string,
  otherUserId: string,
  createdByUserId?: string // Optional: if provided, use this as createdBy instead of currentUserId
): Promise<Group | null> {
  if (currentUserId === otherUserId) return null;

  const members = [currentUserId, otherUserId].sort();
  const groupsRef = collection(db, "groups");
  // Array equality is order-sensitive, and groups created elsewhere don't
  // always store the members sorted: look for both orders so we never open
  // a second private chat between the same two people.
  const [snapshot, reversedSnapshot] = await Promise.all(
    [members, [...members].reverse()].map((order) =>
      getDocs(
        query(
          groupsRef,
          where("isPrivate", "==", true),
          where("members", "==", order)
        )
      )
    )
  );
  const existing = snapshot.docs[0] ?? reversedSnapshot.docs[0];

  if (existing) {
    const docSnap = existing;
    const groupData = docSnap.data() as Group;
    const { id: _, ...groupRest } = groupData;
    return { id: docSnap.id, ...groupRest };
  }

  // Get user data for better group info
  const { findUserById } = await import("./data");
  const otherUser = await findUserById(otherUserId);

  // Create a new group
  // Use createdByUserId if provided (for admin-created groups during onboarding)
  // Otherwise use currentUserId
  const newGroupData = {
    name: "Privatni chat",
    description: otherUser
      ? `Private chat between ${currentUserId} and ${otherUser.name}`
      : `Private chat between ${currentUserId} and ${otherUserId}`,
    createdBy: createdByUserId || currentUserId,
    members,
    avatar: otherUser?.avatar || "/img/logo.png",
    isPrivate: true,
    lastMessage: "Započeli ste privatni razgovor.",
    lastMessageAt: new Date().toISOString(),
  };

  const newGroupRef = await addDoc(groupsRef, newGroupData);
  return { id: newGroupRef.id, ...newGroupData };
}

export async function sendAnonymousThankYouNote(
  recipientId: string,
  message: string
) {
  const BOT_USER_ID = "admin-team-bot";
  // Find or create a private group between recipient and bot placeholder
  const members = [recipientId, BOT_USER_ID].sort();
  const groupsRef = collection(db, "groups");
  const qGroups = query(
    groupsRef,
    where("isPrivate", "==", true),
    where("members", "==", members)
  );
  const snap = await getDocs(qGroups);
  let groupId: string;
  if (!snap.empty) {
    groupId = snap.docs[0].id;
  } else {
    const newGroup = {
      name: `Privatni chat`,
      description: `System chat with ${recipientId}`,
      createdBy: BOT_USER_ID,
      members,
      avatar: "/img/logo.png",
      isPrivate: true,
      lastMessageAt: new Date().toISOString(),
    };
    const newRef = await addDoc(groupsRef, newGroup);
    groupId = newRef.id;
  }
  // Send system message
  await addDoc(collection(db, "groups", groupId, "messages"), {
    type: "system",
    groupId,
    senderId: BOT_USER_ID,
    content: message,
    createdAt: new Date().toISOString(),
  } as any);
}

// -----------------------------------------------------------------------------
// Badges (admin-only)
// -----------------------------------------------------------------------------

export async function toggleUserBadgeAction(
  userId: string,
  badge: "bug_hunter"
) {
  const ref = doc(db, "users", userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const user = snap.data() as User;
  const badges = new Set([...(user.badges || [])]);
  if (badges.has(badge)) badges.delete(badge);
  else badges.add(badge);
  await updateDoc(ref, { badges: Array.from(badges) as any });
}

// -----------------------------------------------------------------------------
// Food actions
// -----------------------------------------------------------------------------

export async function addFoodAction(foodData: Omit<Food, "id">): Promise<Food> {
  // Remove undefined fields, set to null if missing
  const safeFoodData: any = { ...foodData, createdAt: serverTimestamp() };
  Object.keys(safeFoodData).forEach((key) => {
    if (typeof safeFoodData[key] === "undefined") safeFoodData[key] = null;
  });
  const docRef = await addDoc(collection(db, "foods"), safeFoodData);
  return { id: docRef.id, ...foodData };
}

// Foods that used to ship with the app (data/foods.json) had no Firestore
// document, so they couldn't be edited or deleted. copyBuiltInFoodsToFirestore
// gives each one a normal document with the same id; until that has run,
// editing or deleting one creates its document here. A document always
// replaces the app's copy when the list loads (see loadFoods in data.ts).
const builtInFoods = new Map((foodsData as Food[]).map((f) => [f.id, f]));

function builtInFoodDoc(foodId: string): Omit<Food, "id"> | null {
  const food = builtInFoods.get(foodId);
  if (!food) return null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...rest } = food;
  return { ...rest, isGlobal: true };
}

const BUILT_IN_FOODS_COPIED_KEY = "nn-built-in-foods-copied";

/**
 * Staff, once: copies the foods that shipped with the app into Firestore, so
 * every food is a normal, editable one. Skips foods that already have a
 * document (edited or deleted ones included). Returns how many were added.
 */
export async function copyBuiltInFoodsToFirestore(): Promise<number> {
  try {
    if (localStorage.getItem(BUILT_IN_FOODS_COPIED_KEY)) return 0;
  } catch {
    // No storage: just check again.
  }
  const ids = Array.from(builtInFoods.keys());
  const existing = new Set<string>();
  for (let i = 0; i < ids.length; i += 30) {
    const snap = await getDocs(
      query(collection(db, "foods"), where(documentId(), "in", ids.slice(i, i + 30)))
    );
    snap.docs.forEach((d) => existing.add(d.id));
  }
  const missing = ids.filter((id) => !existing.has(id));
  for (let i = 0; i < missing.length; i += 400) {
    const batch = writeBatch(db);
    missing.slice(i, i + 400).forEach((id) => {
      batch.set(doc(db, "foods", id), { ...builtInFoodDoc(id), createdAt: serverTimestamp() });
    });
    await batch.commit();
  }
  try {
    localStorage.setItem(BUILT_IN_FOODS_COPIED_KEY, new Date().toISOString());
  } catch {
    // Fine: it's checked again next time.
  }
  return missing.length;
}

export async function updateFoodAction(
  foodId: string,
  data: Partial<Omit<Food, "id">>
): Promise<void> {
  const ref = doc(db, "foods", foodId);
  const builtIn = builtInFoodDoc(foodId);
  if (builtIn && !(await getDoc(ref)).exists()) {
    await setDoc(ref, { ...builtIn, ...data });
    return;
  }
  await updateDoc(ref, data);
}

export async function deleteFoodAction(foodId: string): Promise<void> {
  await bulkDeleteFoodsAction([foodId]);
}

export async function bulkDeleteFoodsAction(foodIds: string[]): Promise<void> {
  // A batch takes at most 500 writes.
  for (let i = 0; i < foodIds.length; i += 400) {
    const batch = writeBatch(db);
    foodIds.slice(i, i + 400).forEach((id) => {
      const ref = doc(db, "foods", id);
      const builtIn = builtInFoodDoc(id);
      // merge: also fine if the built-in food was edited (has a document).
      if (builtIn) batch.set(ref, { ...builtIn, deleted: true }, { merge: true });
      else batch.update(ref, { deleted: true });
    });
    await batch.commit();
  }
}

export async function bulkAddFoodsAction(
  fileBase64: string,
  createdBy: string,
  isGlobal: boolean = false
): Promise<{ count: number }> {
  // Parse Excel file in browser
  const xlsx = await import("xlsx");
  const buffer = Uint8Array.from(atob(fileBase64), (c) => c.charCodeAt(0));
  const workbook = xlsx.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const data = xlsx.utils.sheet_to_json(sheet, { header: 1 }) as any[];

  const foodsToAdd: Omit<Food, "id">[] = [];
  for (let i = 1; i < data.length; i++) {
    const row = data[i] as any[];
    if (row && row[0] && typeof row[1] === "number") {
      foodsToAdd.push({
        name: String(row[0]),
        calories: Number(row[1]) || 0,
        protein: Number(row[2]) || 0,
        carbs: Number(row[3]) || 0,
        fat: Number(row[4]) || 0,
        createdBy,
        isGlobal,
      });
    }
  }

  const batch = writeBatch(db);
  for (const food of foodsToAdd) {
    const ref = doc(collection(db, "foods"));
    batch.set(ref, food);
  }
  await batch.commit();

  return { count: foodsToAdd.length };
}

// -----------------------------------------------------------------------------
// Training Plan Actions (Client-side)
// -----------------------------------------------------------------------------

export async function applyTemplateToUsersAction(
  userIds: string[],
  template: TrainingPlanTemplate
): Promise<void> {
  const { applyTemplateToUsers } = await import("./data");
  await applyTemplateToUsers(template, userIds);
}

export async function createTrainingPlanTemplateAction(
  template: Omit<TrainingPlanTemplate, "id" | "createdAt">
): Promise<TrainingPlanTemplate> {
  const { createTrainingPlanTemplate } = await import("./data");
  return await createTrainingPlanTemplate(template);
}

export async function deleteUserTrainingPlanAction(
  userId: string,
  planId: string
): Promise<void> {
  const { findUserById } = await import("./data");
  const user = await findUserById(userId);
  if (!user) throw new Error("User not found");

  const updatedPlans = (user.trainingPlans || []).filter(
    (p) => p.id !== planId
  );
  await updateUserProfileWithAdmin(userId, { trainingPlans: updatedPlans });
}

export async function createActivityAction(
  user: User,
  type: Activity["type"],
  metadata?: Activity["metadata"]
): Promise<void> {
  const { collection, addDoc } = await import("@firebase/firestore");
  const { db, auth } = await import("./firebase");

  // Verify user is authenticated - wait up to 1 second for auth to be ready
  let retries = 10;
  while (!auth.currentUser && retries > 0) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    retries--;
  }

  if (!auth.currentUser) {
    const error = new Error("User must be authenticated to create activities");
    console.error(
      "[createActivityAction] Auth check failed - currentUser is null after retries"
    );
    console.error("[createActivityAction] Auth state:", {
      currentUser: auth.currentUser,
      user: user?.id,
      retriesRemaining: retries,
    });
    throw error;
  }

  // Verify the authenticated user matches the user being logged (or is admin/trainer)
  // This is a safety check - the rules will also enforce this
  const authUserId = auth.currentUser.uid;
  if (authUserId !== user.id) {
    console.warn(
      "[createActivityAction] Auth user ID does not match user ID:",
      {
        authUserId,
        userId: user.id,
      }
    );
    // Still allow it - admins/trainers might create activities for other users
    // The Firestore rules will handle the actual permission check
  }

  try {
    const activityData = {
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      type,
      timestamp: new Date().toISOString(),
      metadata: metadata || {},
    };

    console.log("[createActivityAction] Creating activity:", {
      type,
      userId: user.id,
      authUserId: authUserId,
    });

    await addDoc(collection(db, "activities"), activityData);

    console.log("[createActivityAction] Activity created successfully");
  } catch (error: any) {
    console.error("[createActivityAction] Failed to create activity:", error);
    console.error("[createActivityAction] Error details:", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      authUser: auth.currentUser?.uid,
      user: user?.id,
      errorName: error?.name,
    });

    // If it's a permission error, provide more helpful information
    if (
      error?.code === "permission-denied" ||
      error?.message?.includes("permission")
    ) {
      console.error("[createActivityAction] Permission denied. Auth state:", {
        isAuthenticated: !!auth.currentUser,
        authUid: auth.currentUser?.uid,
        userUid: user?.id,
        email: auth.currentUser?.email,
      });
    }

    throw error;
  }
}

export async function applyGoalsToUsersAction(
  goals: User["dailyGoals"],
  userIds: string[]
): Promise<void> {
  if (!goals || userIds.length === 0) return;

  const { doc, writeBatch } = await import("@firebase/firestore");
  const { db } = await import("./firebase");

  const batch = writeBatch(db);
  userIds.forEach((userId) => {
    const userRef = doc(db, "users", userId);
    batch.update(userRef, {
      dailyGoals: goals,
    });
  });

  await batch.commit();
}

type SelectedSlot = { userId: string; slotIndex: number };

// -----------------------------------------------------------------------------
// Custom Exercise actions
// -----------------------------------------------------------------------------

export async function addCustomExerciseAction(exerciseData: {
  title: string;
  description?: string;
  videoUrl?: string;
  createdBy: string;
  bodyTag?: string;
}): Promise<ExerciseBlock> {
  const newExercise = await addExerciseToFileDb({
    title: exerciseData.title,
    description: exerciseData.description,
    videoUrl: exerciseData.videoUrl,
    createdBy: exerciseData.createdBy,
    bodyTag: exerciseData.bodyTag,
  });

  // Convert LibraryExercise to ExerciseBlock
  const newBlock: ExerciseBlock = {
    id: newExercise.id,
    type: "exercise" as const,
    title: newExercise.title,
    description: newExercise.description || "",
    videoUrl: newExercise.videoUrl || "",
    sets: 4,
    reps: 12,
    restBetweenSets: 90,
    duration: 250, // Default duration
    bodyTag: newExercise.bodyTag,
  };

  return newBlock;
}

export async function updateCustomExerciseAction(
  exerciseId: string,
  exerciseData: {
    title: string;
    description?: string;
    videoUrl?: string;
    bodyTag?: string;
  }
): Promise<void> {
  await updateExerciseInFileDb(exerciseId, exerciseData);
}

export async function deleteCustomExerciseAction(
  exerciseId: string
): Promise<void> {
  await deleteExerciseFromFileDb(exerciseId);
}

export async function applyTemplateToSpecificSlotsAction(
  selectedSlots: SelectedSlot[],
  template: TrainingPlanTemplate
): Promise<void> {
  const { v4: uuidv4 } = await import("uuid");
  const { findUserById } = await import("./data");
  const { doc, writeBatch } = await import("@firebase/firestore");
  const { db } = await import("./firebase");

  // A fresh copy (new plan/day/block ids) per slot: sharing one object made
  // two slots of the same user tick and delete together.
  const buildPlan = (): UserTrainingPlan => ({
    id: uuidv4(),
    name: template.name,
    description: template.description,
    assignedAt: new Date().toISOString(),
    startWeek: 0,
    endWeek: 4,
    days: template.plan.map((day) => ({
      ...day,
      completed: false,
      id: uuidv4(),
      blocks: (day.blocks || []).map((block) => {
        if (block.type === "loop") {
          return {
            ...block,
            id: uuidv4(),
            blocks: (block.blocks || []).map((child) => ({
              ...child,
              id: uuidv4(),
            })),
          };
        }
        return { ...block, id: uuidv4() };
      }),
    })),
  });

  const usersToUpdate = new Map<string, { doc: User; slots: number[] }>();

  for (const slot of selectedSlots) {
    if (!usersToUpdate.has(slot.userId)) {
      const userDoc = await findUserById(slot.userId);
      if (userDoc) {
        usersToUpdate.set(slot.userId, { doc: userDoc, slots: [] });
      }
    }
    usersToUpdate.get(slot.userId)?.slots.push(slot.slotIndex);
  }

  const batch = writeBatch(db);

  for (const [userId, { doc: userDoc, slots }] of usersToUpdate.entries()) {
    const userRef = doc(db, "users", userId);
    let currentPlans = [...(userDoc.trainingPlans || [])];

    // Replace or add the plan at the specified slots
    slots.forEach((slotIndex) => {
      if (slotIndex >= 0 && slotIndex < 3) {
        // This will either add at the index or override what's there
        currentPlans[slotIndex] = buildPlan();
      }
    });

    // Ensure the array doesn't exceed 3 and filter out any potential empty spots
    // that might have been created if the original array was shorter.
    const finalPlans = currentPlans.filter((p) => p).slice(0, 3);

    // sanitize: an undefined description made the whole batch throw.
    batch.update(userRef, sanitizeForFirestore({ trainingPlans: finalPlans }));
  }

  await batch.commit();
}
